import React, { useEffect, useState } from "react";

const defaultEnd = (startISO) => {
  if (!startISO) return "";
  const start = new Date(startISO);
  if (Number.isNaN(start)) return "";
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return end.toISOString();
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const toISOFromLocal = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  const offset = d.getTimezoneOffset();
  const utc = new Date(d.getTime() + offset * 60 * 1000);
  return utc.toISOString();
};

export default function BookingRequestForm({
  initialSpace = "BANQUET_HALL",
  initialStart,
  initialEnd,
  onSubmitted,
}) {
  const [form, setForm] = useState({
    space: initialSpace,
    start_at: initialStart || "",
    end_at: initialEnd || defaultEnd(initialStart),
    name: "",
    email: "",
    phone: "",
    description: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      space: initialSpace || prev.space,
      start_at: initialStart || prev.start_at,
      end_at: initialEnd || defaultEnd(initialStart || prev.start_at),
    }));
  }, [initialSpace, initialStart, initialEnd]);

  const onChange = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    setQuote(null);

    try {
      const payload = {
        ...form,
        start_at: form.start_at,
        end_at: form.end_at,
      };
      const res = await fetch("/api/booking-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Request failed");
      }
      setSuccess("Request received! We will confirm availability soon.");
      setQuote(data?.quote || null);
      if (onSubmitted) onSubmitted(data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="booking-form card">
      <h3>Request a Booking</h3>
      <p className="text-muted" style={{ marginTop: -4 }}>
        We will review and confirm availability. Times are in America/Winnipeg.
      </p>
      <form onSubmit={handleSubmit} className="booking-form-grid">
        <label>
          Space
          <select
            value={form.space}
            onChange={(e) => onChange("space", e.target.value)}
            required
          >
            <option value="BANQUET_HALL">Banquet Hall</option>
            <option value="BOARDROOM">Boardroom</option>
          </select>
        </label>

        <label>
          Start
          <input
            type="datetime-local"
            required
            value={toLocalInput(form.start_at)}
            onChange={(e) => onChange("start_at", toISOFromLocal(e.target.value))}
          />
        </label>

        <label>
          End
          <input
            type="datetime-local"
            required
            value={toLocalInput(form.end_at)}
            min={toLocalInput(form.start_at)}
            onChange={(e) => onChange("end_at", toISOFromLocal(e.target.value))}
          />
        </label>

        <label>
          Name
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="Your full name"
          />
        </label>

        <label>
          Email
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="you@example.com"
          />
        </label>

        <label>
          Phone
          <input
            type="tel"
            required
            value={form.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            placeholder="(204) 555-1234"
          />
        </label>

        <label className="booking-form__full">
          Event description
          <textarea
            required
            rows={3}
            value={form.description}
            onChange={(e) => onChange("description", e.target.value)}
            placeholder="Tell us about your event"
          />
        </label>

        <label style={{ display: "none" }}>
          Website
          <input
            type="text"
            value={form.website}
            onChange={(e) => onChange("website", e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>

        <div className="booking-form__actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
          {success ? <span className="text-success">{success}</span> : null}
          {error ? <span className="text-error">{error}</span> : null}
          {quote ? (
            <span className="text-muted">
              Quote:{" "}
              {"amount" in quote
                ? `${quote.amount} ${quote.currency || "CAD"} (estimate)`
                : quote.note || "Pending review"}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
